import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import { ChipListProps } from '@components/ChipList'
import SelectWithLabeledValue from '@components/SelectWithLabeledValue'
import { EVENT_ACTIVITY_KEYS } from '@modules/activity/activityKeys'
import { useTrackUserActivity } from '@modules/activity/useTrackUserActivity'
import useAlertDataForProfiles from '@modules/alerts/hooks/useAlertDataForProfiles'
import useContactWithProfilesCounts from '@modules/contacts/hooks/useContactWithProfilesCounts'
import useContactable from '@modules/profile/hooks/useContactable'
import { SEARCH_GROUP_BY_OPTIONS, SORT_SEARCH_OPTIONS } from '@modules/search/constants'
import { SearchRequestBody } from '@modules/search/hooks/useSearchProfiles'
import {
  AnyResultProfile,
  GroupedResultProfile,
  HISTORY_FILTER_OPTIONS_LABELS,
  HistoryFilterOptions,
  MAX_EVENTS_CHIPS_TO_SHOW,
  MetaFilterSelectedData,
  MetaFilterSelectedDate,
  MetaFilterTypes,
  ProfileType,
  SearchProfilesResponse,
  SelectedFiltersMapByProfiles,
  UserEventsOptions,
} from '@modules/search/types'
import { Pagination, PaginationItem, PaginationRenderItemParams } from '@mui/material'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { truncateWithDots } from '@shared/utils'

import GroupedResultProfileList from './components/GroupedResultProfileList'
import { NewItemsSelectFilter } from './components/NewItemsSelectFilter'
import ResultChips from './components/ResultChips'
import ResultProfileList from './components/ResultProfileList'
import SearchResultPlaceholder from './components/SearchResultPlaceholder'
import {
  SearchResultsFiltersWrap,
  SearchResultsSectionInnerWrapper,
  SearchResultsSectionWrapper,
  SearchResultsTopPanel,
  TopPaginationWrapper,
} from './index.styles'
import { getCompanyId } from './util/ResultProfile.util'

export interface ResultsSectionProps {
  resultProfiles: SearchProfilesResponse
  onDeleteChip: ChipListProps['onDelete']
  searchString: string
  total: number
  selectedFiltersByProfiles: SelectedFiltersMapByProfiles
  totalPages: number
  page: number
  setPage: (page: number) => void
  setScrollX: (page: number) => void
  setScrollY: (page: number) => void
  onSort: (id: number, withPageReset?: boolean) => void
  onGroup: (id: number, withPageReset?: boolean) => void
  selectedSortId: number
  sortDisabled: boolean
  selectedMetaFilter: MetaFilterSelectedData
  isGroupByDisabled: boolean
  selectedGroupingId?: number
  isResultGrouped?: boolean
  isFilterApplied?: boolean
  appliedSavedSearchName?: string
  onProfileNavigate?: (id: string | number) => void
  savedSearchId?: number
  userEventsOptions?: UserEventsOptions[]
  eventsFilterData: { excludeIds: number[]; includeIds: number[] } | null
  searchBody?: SearchRequestBody
  searchHistoryTypeId?: number | undefined
  selectedNewItemsFilter: boolean
  showNewItemsFilter: boolean
  handleSelectNewItemsFilter?: (isSelected: boolean) => void
}

const ResultsSection = (props: ResultsSectionProps) => {
  const {
    resultProfiles,
    searchString,
    onDeleteChip,
    total,
    totalPages,
    page,
    setPage,
    setScrollX,
    setScrollY,
    selectedFiltersByProfiles = {} as SelectedFiltersMapByProfiles,
    onSort,
    selectedSortId,
    sortDisabled,
    selectedMetaFilter,
    isGroupByDisabled,
    onGroup,
    selectedGroupingId,
    isResultGrouped,
    isFilterApplied,
    appliedSavedSearchName,
    onProfileNavigate,
    savedSearchId,
    userEventsOptions = [],
    eventsFilterData,
    searchBody,
    searchHistoryTypeId,
    selectedNewItemsFilter,
    showNewItemsFilter,
    handleSelectNewItemsFilter,
  } = props

  const { t } = useTranslation()

  const { hasAccessToAlerts } = useFeatureFlags()

  const countsByCompanyId = useContactWithProfilesCounts({ profiles: resultProfiles })
  const profiles = useMemo(() => {
    if (isResultGrouped) {
      return (resultProfiles as GroupedResultProfile[]).flatMap(profile => [profile.company, ...profile.profiles])
    }
    return resultProfiles as AnyResultProfile[]
  }, [resultProfiles, isResultGrouped])

  const { profileAlerts, profilesAlertsMap } = useAlertDataForProfiles(profiles, hasAccessToAlerts && !!profiles?.length)

  const contactableMap = useContactable(
    resultProfiles.map(i => Number(getCompanyId((i as GroupedResultProfile)?.company || i))).filter(id => !!id),
  )

  const formatChipsFromFilters = (filterList: { field: string; value: string }[], profileType: ProfileType) =>
    filterList.map(({ value, field }) => ({
      label: truncateWithDots(value.replace(/^[^::]*::/, '')),
      key: value,
      metaData: { filterType: profileType || '', field },
    }))

  const metaFilterChip = useMemo(() => {
    if (selectedMetaFilter && selectedMetaFilter.type !== MetaFilterTypes.DEFAULT) {
      let label = ''
      switch (selectedMetaFilter.type) {
        case MetaFilterTypes.BY_RECENT:
          label = t(selectedMetaFilter.value as string)
          break
        case MetaFilterTypes.BY_DATE_RANGE:
          label = `${t(translationKeys.SEARCH_FILTER_BY_DATE_RANGE)}: ${
            (selectedMetaFilter.value as MetaFilterSelectedDate).from
          } - ${(selectedMetaFilter.value as MetaFilterSelectedDate).to}`
          break
        default:
          label = selectedMetaFilter.type
      }

      return {
        label,
        key: 'meta_filter',
        metaData: {
          removeMetaFilter: true,
        },
      }
    }

    return null
  }, [selectedMetaFilter, t])

  const historyFilterChip = useMemo(() => {
    if (searchHistoryTypeId) {
      return {
        label: t(HISTORY_FILTER_OPTIONS_LABELS[searchHistoryTypeId as HistoryFilterOptions] as string),
        key: HISTORY_FILTER_OPTIONS_LABELS[searchHistoryTypeId as HistoryFilterOptions] as string,
        metaData: {
          removeHistoryFilter: true,
        },
      }
    }

    return null
  }, [searchHistoryTypeId, t])

  const eventFilterChips = useMemo(() => {
    if (eventsFilterData?.includeIds || eventsFilterData?.excludeIds) {
      const eventsTotal = (eventsFilterData?.includeIds?.length || 0) + (eventsFilterData?.excludeIds?.length || 0)
      if (eventsTotal > MAX_EVENTS_CHIPS_TO_SHOW) {
        return [
          {
            label: t(translationKeys.MULTIPLE_EVENTS_FILTERED),
            key: translationKeys.MULTIPLE_EVENTS_FILTERED,
            metaData: {
              removeEventFilter: true,
            },
          },
        ]
      }

      const eventChips = []

      eventChips.push(
        ...(eventsFilterData?.includeIds.map(includedEventId => ({
          label: userEventsOptions.find(event => event.value === includedEventId)?.label as string,
          key: includedEventId.toString(),
          metaData: {
            removeIncludedEventId: includedEventId,
          },
        })) || []),
      )

      eventChips.push(
        ...(eventsFilterData?.excludeIds.map(excludedEventId => ({
          label: `${t(translationKeys.FILTER_BY_EXCLUDE)}: ${
            userEventsOptions.find(event => event.value === excludedEventId)?.label
          }`,
          key: excludedEventId.toString(),
          metaData: {
            removeExcludedEventId: excludedEventId,
          },
        })) || []),
      )

      return eventChips
    }

    return []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsFilterData, t])

  const chips = useMemo(() => {
    const filterEntries = Object.entries(selectedFiltersByProfiles)
    const extraFilterEntries: { label: string; key: string }[] = []
    if (metaFilterChip) {
      extraFilterEntries.push(metaFilterChip)
    }
    if (historyFilterChip) {
      extraFilterEntries.push(historyFilterChip)
    }

    if (eventFilterChips?.length) {
      extraFilterEntries.push(...eventFilterChips)
    }

    return [
      ...(filterEntries.flatMap(([profile, selectedProfileFilters]) =>
        formatChipsFromFilters(selectedProfileFilters, profile as ProfileType),
      ) || []),
      ...extraFilterEntries,
    ]
  }, [selectedFiltersByProfiles, metaFilterChip, historyFilterChip, eventFilterChips])

  const getPaginationClass = (item: PaginationRenderItemParams) => {
    const classMap: Record<string, string> = {
      page: `search_results-page_${item.page ?? ''}`,
      previous: 'search-navigation-left_arrow',
      next: 'search-navigation-right_arrow',
    }
    return classMap[item.type] || ''
  }

  const handlePaginationChange = (e: React.ChangeEvent<unknown>, v: number) => {
    setPage(v)

    if (window.scrollY > 250) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const { trackActivity } = useTrackUserActivity()

  const isSearchResultEmpty = !chips.length && !searchString
  return (
    <SearchResultsSectionWrapper>
      <SearchResultsSectionInnerWrapper>
        {!!(chips.length || searchString) && (
          <ResultChips
            chips={chips}
            onDelete={onDeleteChip}
            resultCount={total}
            searchString={searchString}
            isFilterApplied={isFilterApplied}
            appliedSavedSearchName={appliedSavedSearchName}
            searchBody={searchBody}
          />
        )}
        {isSearchResultEmpty && <SearchResultPlaceholder />}

        {!isSearchResultEmpty && (
          <SearchResultsTopPanel>
            <>
              <TopPaginationWrapper>
                {!isSearchResultEmpty && totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePaginationChange}
                    data-testid="search_result_pagination"
                    renderItem={item => (
                      <PaginationItem
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...item}
                        className={getPaginationClass(item)}
                      />
                    )}
                  />
                )}
              </TopPaginationWrapper>
              <SearchResultsFiltersWrap>
                {(!isSearchResultEmpty || !!showNewItemsFilter) && (
                  <>
                    {!!showNewItemsFilter && (
                      <NewItemsSelectFilter
                        value={selectedNewItemsFilter}
                        handleSelectNewItemsFilter={handleSelectNewItemsFilter}
                      />
                    )}
                    <SelectWithLabeledValue
                      containerId="search-sort-atoz"
                      disabled={sortDisabled}
                      label={t(translationKeys.SEARCH_SORT_BY)}
                      onSelect={(value: number, option?: { label: string }) => {
                        onSort(value as number)
                        if (option) {
                          trackActivity(EVENT_ACTIVITY_KEYS.SEARCH.SORT.CLICK, { selectedOption: t(option.label) })
                        }
                      }}
                      options={SORT_SEARCH_OPTIONS}
                      selectedOptionId={selectedSortId}
                    />
                    <SelectWithLabeledValue
                      containerId="search-sort-group_by"
                      disabled={isGroupByDisabled}
                      label={t(translationKeys.SEARCH_GROUP_BY)}
                      onSelect={value => onGroup(value as number)}
                      options={SEARCH_GROUP_BY_OPTIONS}
                      emptySelection
                      emptySelectionLabel={t(translationKeys.NO_GROUPING)}
                      selectedOptionId={selectedGroupingId}
                    />
                  </>
                )}
              </SearchResultsFiltersWrap>
            </>
          </SearchResultsTopPanel>
        )}
        {!isSearchResultEmpty &&
          (isResultGrouped ? (
            <GroupedResultProfileList
              groups={resultProfiles as GroupedResultProfile[]}
              companyContactsMap={countsByCompanyId}
              contactableMap={contactableMap}
              setScrollX={setScrollX}
              setScrollY={setScrollY}
            />
          ) : (
            <ResultProfileList
              profiles={resultProfiles as AnyResultProfile[]}
              companyContactsMap={countsByCompanyId}
              contactableMap={contactableMap}
              onProfileNavigate={onProfileNavigate}
              savedSearchId={savedSearchId}
              profilesAlertsMap={profilesAlertsMap}
              setScrollX={setScrollX}
              setScrollY={setScrollY}
            />
          ))}

        {!isSearchResultEmpty && totalPages > 1 && profileAlerts && (
          <Pagination count={totalPages} page={page} onChange={handlePaginationChange} />
        )}
      </SearchResultsSectionInnerWrapper>
    </SearchResultsSectionWrapper>
  )
}

export default memo(ResultsSection)
